import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AccessRole, ButtonColumnType, CustomTableComponent, DataColumnType, ICustomTable, IDropdownSelect, IHeader, LoadingPanelService, LocalService, UserInfoResponse } from 'lib-base';
import { ConfirmationService, MessageService } from 'primeng/api';
import { firstValueFrom, forkJoin } from 'rxjs';
import { SecPermisoService } from '../../../../services/database/secPermiso.service';
import { IAddPermisosToCargoRequest, ISecPermiso } from '../../../../shared/interfaces/database/seguridad';
import { SecCargoPermisoService } from '../../../../services/database/secCargoPermiso.service';
import { AdminAuthService } from '../../../../services/database/adminAuth.service';
import { CargoPermisoHistoricoComponent } from './cargo-permiso-historico.component';

@Component({
  selector: 'app-admin-cargo-permiso',
  standalone: true,
  imports: [CustomTableComponent, CargoPermisoHistoricoComponent],
  template: `
  <custom-table [config]="customTable">
  </custom-table>
  <app-cargo-permiso-historico></app-cargo-permiso-historico>
  `
})
export class AdminCargoPermisoComponent implements OnInit {

  @Input() cargoId!: number;
  trans: any = {};
  role = AccessRole;

  permisos: ISecPermiso[] = [];
  customTable !: ICustomTable;
  @ViewChild(CustomTableComponent) customTableComponent !: CustomTableComponent;
  @ViewChild(CargoPermisoHistoricoComponent) historico !: CargoPermisoHistoricoComponent;

  constructor(public readonly ls: LocalService, private readonly _lps: LoadingPanelService, private readonly _t: TranslateService, private readonly _cs: ConfirmationService, private readonly _sps: SecPermisoService,
    private readonly _scp: SecCargoPermisoService, private readonly _ms: MessageService, private readonly _localService: LocalService, 
    private readonly _as: AdminAuthService
  ) { }

  ngOnInit(): void {
    this.translate();
    this.getSecPermisosByCargoId();
    this.getSecPermisosNoEnCargo();
  }

  private async translate(): Promise<void> {
    this.trans = await firstValueFrom(forkJoin({
      codificacion: this._t.get('common.codificacion'),
      descripcion: this._t.get('common.descripcion'),
      tipo: this._t.get('common.tipo'),
      exito: this._t.get('common.exito'),
      permisos: this._t.get('common.permisos'),
      permiso: this._t.get('common.permiso'),
      estasSeguroEliminar: this._t.get('msg.estasSeguroEliminar'),
      permisoEliminadoMensaje: this._t.get('msg.permisoEliminadoMensaje'),
      permisoAgregadoMensaje: this._t.get('msg.permisoAgregadoMensaje'),
      requiereRolSeguridad: this._t.get('msg.requiere-rol-seguridad'),
    }));

    this.customTable = {
      header: {
        title: this.trans.permisos,
        class: 'title font-roboto-condensed font-18 title-blue text-uppercase font-normal mb-0',
      } as IHeader,
      columns: [
        {
          field: 'codificacion',
          header: this.trans.codificacion,
          sort: true,
          class: 'font-13',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: ''
          },
          columnFilter: false,
          dataType: DataColumnType.TEXT
        },
        {
          field: 'descripcion',
          header: this.trans.descripcion,
          sort: true,
          class: 'font-13',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: ''
          },
          columnFilter: false,
          dataType: DataColumnType.TEXT
        },
        {
          field: 'tipo',
          header: this.trans.tipo,
          sort: true,
          class: 'font-13',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: ''
          },
          columnFilter: true,
          dataType: DataColumnType.TEXT
        },
        {
          field: 'delete',
          header: '',
          width: '0rem',
          class: 'pt-2 pb-2 pl-0',
          buttonType: ButtonColumnType.DELETE,
          disabled: !this.ls.hasRole(this.role.ADMIN_TRAM),
          tooltip: {
            message: this.trans.requiereRolSeguridad
          },
          onClick: (event: Event, data: ISecPermiso) => this.deleteConfirmation(event, data),
          headerBtn: {
            icon: 'pi pi-history',
            class: 'pt-2 pb-2 pl-1 pr-1',
            severity: 'info',
            onClick: (event: Event) => this.abrirHistorial(),
          }
        }
      ],
      settings: {
        table: {
          rows: 5,
        }
      }
    } as ICustomTable;
  }

  deleteConfirmation(event: Event, data: ISecPermiso) {
    this._cs.confirm({
      target: event.target as EventTarget,
      message: this.trans.estasSeguroEliminar + this.trans.permiso + '?',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.removePermisosFromCargo(data.id);
      }
    });
  }

  onDropdownBtnClick(event: Event, data: any) {
    let request = {
      cargoId: this.cargoId,
      permisosId: [data.id]
    } as IAddPermisosToCargoRequest;

    this.addPermisosToCargo(request);
  }

  //#region historico

  abrirHistorial(): void {
    this.historico.show(this.cargoId);
  }

  //#endregion historico

  //#region API Calls
  getSecPermisosByCargoId() {
    if (!this.cargoId)
      return;
    this._sps.getSecPermisosByCargoId(this.cargoId).subscribe({
      next: (data: ISecPermiso[]) => {
        this.permisos = data;
        this.customTableComponent.data = data;
      }
    });
  }

  removePermisosFromCargo(permisoId: number) {
    this._lps.show(true);
    this._scp.removePermisosFromCargo(this.cargoId, permisoId).subscribe({
      next: (response: boolean) => {
        if(response){
          this.getUserInfo();
        }
        this._lps.hide();
        this._ms.add({ severity: 'success', summary: this.trans.exito, detail: this.trans.permisoEliminadoMensaje });
        this.getSecPermisosNoEnCargo();
        this.getSecPermisosByCargoId();
      }
    });
  }

  getUserInfo() {
    this._as.getUserInfo().subscribe({
      next: (response: UserInfoResponse) => {
        this._localService.saveUser(response);
      }
    });
  }

  getSecPermisosNoEnCargo() {
    if (!this.cargoId)
      return;
    this._sps.getSecPermisosNoEnCargo(this.cargoId).subscribe({
      next: (data: ISecPermiso[]) => {
        let dropdownData = data.map((d: ISecPermiso) => {
          return{
            ...d,
            label: d.codificacion + " (" + d.tipo + ")",
          };
        });

        let dropdown = {
          data: dropdownData,
          label: 'label',
          disabled: !this.ls.hasRole(this.role.ADMIN_SEC),
          onBtnClick: (event: Event, data: any) => this.onDropdownBtnClick(event, data),
        } as IDropdownSelect

        this.customTableComponent.setHeaderDropdownSelect(dropdown);
      }
    });
  }

  addPermisosToCargo(request: IAddPermisosToCargoRequest) {
    this._lps.show(true);
    this._scp.addPermisosToCargo(request).subscribe({
      next: (response: boolean) => {
        if(response){
          this.getUserInfo();
        }
        this._lps.hide();
        this._ms.add({ severity: 'success', summary: this.trans.exito, detail: this.trans.permisoAgregadoMensaje });
        this.getSecPermisosNoEnCargo();
        this.getSecPermisosByCargoId();
      }
    })
  }
  //#endregion
}
